import {useEffect, useState} from 'react';
import {useNavigate, useParams, useLocation} from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import {ArrowBack} from '@mui/icons-material';
import {
  backButtonStyles,
  headerTitleStyles,
  verticalDividerStyles,
} from '../PaymentDetails/styles';
import {
  useCreateInvoice,
  useGenerateInvoice,
  useUpdateInvoice,
  useSaveInvoice,
} from './hooks';
import {usePaymentDetails} from '../PaymentDetails/hooks';
import {useClaimDetails} from '../InsuranceClaims/hooks';
import moment, {formatDate} from '~/utils/date';
import {TEXT_CONSTANTS} from './constants';
import Loading from '~/components/Loading';
import {Color} from '~/constants/style';
import PDFViewer from '~/components/PDFViewer';
import {
  Content,
  InvoiceDetailsContentWrapperStyles,
  InvoiceDetailsStyles,
  Main,
  SaveInvoiceButtonStyles,
  SaveInvoiceButtonWrapperStyles,
  VerticalDivider,
  InfoFieldValueStyles,
  noItemsPresentTextStyles,
  patientDetailsBoxStyles,
  invoiceMetaBoxStyles,
  itemsToBillBoxStyles,
  invoiceTitleStyles,
  itemsToBillItemBoxStyles,
} from './styles';
import {IInvoiceDetails, IInvoiceItem} from '~/api/finance/invoice';
import FrontdeskButton from '~/components/FrontdeskButton';
import PlusSquareIcon from '$assets/svgs/plus-square.svg?react';
import useAuthUser from '~/hooks/useAuthUser';
import {UserRoleName} from '$modules/admin/constant';
import AddPaymentItem from '../../common/components/AddPaymentItem';
import {Consultant, useConsultants} from '../../hooks';
import SelectInput from '~/components/SelectInput';

interface IUserParams extends Record<string, string> {
  id: string;
}

interface IInvoiceDetailsParams {
  invoiceData: IInvoiceDetails;
}

const PatientDetails = ({invoiceData}: IInvoiceDetailsParams) => {
  const {patient, insurance_claim} = invoiceData || {};
  const {address, full_name, dob} = patient || {};
  const {line1, line2, city_name, state, country_name, postcode} =
    address || {};

  const completeAddress = [line1, line2, city_name, state, country_name]
    .filter(Boolean)
    .join(', ');

  const fields = [
    {label: TEXT_CONSTANTS.NAME, value: full_name},
    {label: TEXT_CONSTANTS.ADDRESS, value: completeAddress},
    {label: TEXT_CONSTANTS.POST_CODE, value: postcode},
    {label: TEXT_CONSTANTS.DATE_OF_BIRTH, value: formatDate(dob)},
    {
      label: TEXT_CONSTANTS.MEMBERSHIP_NUMBER,
      value: insurance_claim?.membership_no || '-',
    },
    {
      label: TEXT_CONSTANTS.AUTHORIZATION_CODE,
      value: insurance_claim?.pre_authorisation_code || '-',
    },
  ];

  return (
    <Grid item sx={{flex: 1}}>
      <Box sx={patientDetailsBoxStyles}>
        <Stack spacing={2}>
          <Typography variant="h4" sx={invoiceTitleStyles}>
            {TEXT_CONSTANTS.PATIENT_DETAILS}
          </Typography>
          {fields.map(field => (
            <InfoField
              key={field.label}
              label={field.label}
              value={field.value}
            />
          ))}
        </Stack>
      </Box>
    </Grid>
  );
};

const InvoiceMeta = ({
  invoiceData,
  consultants,
  onConsultantChange,
  showError = false,
}: IInvoiceDetailsParams & {
  consultants: Consultant[];
  onConsultantChange: (consultantId: number) => void;
  showError?: boolean;
}) => {
  const {issued_at} = invoiceData || {};
  const canEditInvoice = !invoiceData.invoice_issued_at;

  const handleConsultantChange = (value: string) => {
    onConsultantChange(Number(value));
  };

  return (
    <Grid item sx={{flex: 1}}>
      <Box sx={invoiceMetaBoxStyles}>
        <Stack spacing={2}>
          <Typography variant="h4" sx={invoiceTitleStyles}>
            {TEXT_CONSTANTS.INVOICE_DETAILS}
          </Typography>
          <InfoField
            label={TEXT_CONSTANTS.INVOICE_DATE}
            value={formatDate(issued_at)}
          />
          <InfoField label={TEXT_CONSTANTS.CONSULTANT} value="" />
          <SelectInput
            value={String(invoiceData.consultant?.id || '')}
            items={consultants}
            onChange={handleConsultantChange}
            fullWidth
            disabled={!canEditInvoice}
            placeholder={
              invoiceData?.consultant?.id
                ? 'Select a consultant'
                : 'Select a consultant *'
            }
            error={showError}
            renderValue={value => {
              if (!value || value === '') {
                return 'Select a consultant *';
              }
              const consultant = consultants.find(c => String(c.id) === value);
              return consultant?.fullName || '';
            }}
            renderItem={(consultant: Consultant) => (
              <MenuItem key={consultant.id} value={String(consultant.id)}>
                {consultant.fullName}
              </MenuItem>
            )}
          />
          {showError && (
            <Typography color="error" variant="caption">
              {TEXT_CONSTANTS.CONSULTANT_REQUIRED}
            </Typography>
          )}
        </Stack>
      </Box>
    </Grid>
  );
};

const ItemsToBill = ({
  invoiceData,
  setIsSearchPaymentItemOpen,
}: IInvoiceDetailsParams & {
  setIsSearchPaymentItemOpen: (open: boolean) => void;
}) => {
  const items =
    invoiceData?.payment?.items || invoiceData?.insurance_claim?.items || [];

  const {roleName} = useAuthUser()!;
  const isFinanceRole = roleName === UserRoleName.Finance;

  const canAddItems = !invoiceData.invoice_issued_at;

  const isUnpaidPayment = invoiceData?.payment
    ? String(invoiceData.payment.status) === 'unpaid'
    : false;

  const canAddToInsuranceClaim = invoiceData?.insurance_claim
    ? invoiceData.insurance_claim.status === 'new_claim' ||
      invoiceData.insurance_claim.status === 'pending'
    : false;

  const showAddItemButton =
    isFinanceRole && canAddItems && (isUnpaidPayment || canAddToInsuranceClaim);

  return (
    <Grid item sx={{flex: 1}}>
      <Box sx={itemsToBillBoxStyles}>
        <Stack spacing={3}>
          <Typography variant="h4" sx={invoiceTitleStyles}>
            {TEXT_CONSTANTS.ITEMS_TO_BILL}
          </Typography>
          {items.length > 0 ? (
            items.map((item: IInvoiceItem, index: number) => (
              <Box key={index} sx={itemsToBillItemBoxStyles}>
                <Box>
                  <InfoField
                    label={TEXT_CONSTANTS.ITEM_DESCRIPTION}
                    value={item.description || '-'}
                    valueSx={InfoFieldValueStyles}
                  />
                </Box>
                <Box>
                  <InfoField
                    label={TEXT_CONSTANTS.PROCEDURE_PRICE}
                    value={`£${item.amount || '0.00'}`}
                    valueSx={InfoFieldValueStyles}
                  />
                </Box>
              </Box>
            ))
          ) : (
            <Typography sx={noItemsPresentTextStyles}>
              No items present
            </Typography>
          )}
          {showAddItemButton && (
            <FrontdeskButton
              variant="primary"
              title="Add item"
              startIcon={<PlusSquareIcon width={20} height={20} />}
              className="w-fit font-normal text-[14px] mt-2"
              onClick={() => setIsSearchPaymentItemOpen(true)}
            />
          )}
        </Stack>
      </Box>
    </Grid>
  );
};

const InfoField = ({
  label,
  value,
  valueSx,
}: {
  label: string;
  value: string | number;
  valueSx?: object;
}) => (
  <Box>
    <Typography variant="subtitle1" sx={{mb: 1}}>
      {label}
    </Typography>
    <Box sx={valueSx}>
      <Typography variant="subtitle2">{value}</Typography>
    </Box>
  </Box>
);

function InvoiceDetailsComponent() {
  const navigate = useNavigate();
  const location = useLocation();
  const {id} = useParams<IUserParams>();

  const isInsuranceClaim = location.pathname.includes('insurance-claims');
  const backRoute = location?.state?.backRoute;

  const {payment, isLoading: isPaymentLoading} = usePaymentDetails(
    !isInsuranceClaim ? id || '' : ''
  );
  const {data: insuranceClaim, isLoading: isClaimLoading} = useClaimDetails(
    isInsuranceClaim ? id : undefined
  );

  const isLoading = isPaymentLoading || isClaimLoading;
  const {isInvoiceGenerating, generateInvoice, pdfData} = useGenerateInvoice();
  const {
    createInvoice,
    isPending,
    invoiceData: initialInvoiceData,
  } = useCreateInvoice({showNotification: false});
  const {consultants} = useConsultants();

  const navigateToDetailsPage = () => {
    try {
      if (currentInvoiceData?.insurance_claim?.id) {
        navigate(-1);
      } else if (payment?.id || currentInvoiceData?.payment?.id) {
        navigate(-1);
      } else {
        console.warn('No valid navigation target found');
        navigate(-1);
      }
    } catch (error) {
      console.error('Error during navigation:', error);
      navigate(-1);
    }
  };
  const {saveInvoice} = useSaveInvoice({
    onSuccess: response => {
      if (response.success && pdfData) {
        downloadPDF(
          pdfData,
          response.data.invoice.invoice_number,
          response.data.invoice.issued_at
        );
        setTimeout(() => {
          navigateToDetailsPage();
        }, 100);
      }
    },
  });

  const [invoicePDFData, setInvoicePDFData] = useState<string>();
  const [isSearchPaymentItemOpen, setIsSearchPaymentItemOpen] = useState(false);
  const [currentInvoiceData, setCurrentInvoiceData] =
    useState<IInvoiceDetails | null>(null);
  const {updateInvoice} = useUpdateInvoice();

  const navigateBack = () => {
    if (backRoute) {
      navigate(backRoute);
    } else {
      navigate(-1);
    }
  };

  const downloadPDF = (
    pdfBlob: Blob,
    invoiceNumber: string,
    issuedAt: string
  ) => {
    const url = window.URL.createObjectURL(pdfBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Invoice_${invoiceNumber}_${moment(issuedAt).format('DD-MM-YYYY')}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleSaveInvoice = () => {
    if (!pdfData || !currentInvoiceData) return;

    if (!currentInvoiceData.consultant?.id) {
      return;
    }

    if (!currentInvoiceData.invoice_issued_at) {
      saveInvoice({invoice_id: currentInvoiceData.id});
    } else {
      downloadPDF(
        pdfData,
        currentInvoiceData.invoice_number,
        currentInvoiceData.issued_at
      );
      setTimeout(() => {
        navigateToDetailsPage();
      }, 100);
    }
  };

  const handleConsultantChange = (consultantId: number) => {
    if (!currentInvoiceData) return;

    const selectedConsultant = consultants.find(c => c.id === consultantId);
    if (selectedConsultant) {
      setCurrentInvoiceData(prev =>
        prev
          ? {
              ...prev,
              consultant: {
                id: selectedConsultant.id,
                full_name: selectedConsultant.fullName,
              },
            }
          : null
      );
    }

    updateInvoice(
      {invoice_id: currentInvoiceData.id, consultant_id: consultantId},
      {
        onSuccess: data => {
          if (data?.data?.invoice) setCurrentInvoiceData(data.data.invoice);
          setInvoicePDFData(undefined);
          generateInvoice({invoice_id: currentInvoiceData.id});
        },
        onError: () => {
          if (initialInvoiceData) setCurrentInvoiceData(initialInvoiceData);
        },
      }
    );
  };

  useEffect(() => {
    if (initialInvoiceData) setCurrentInvoiceData(initialInvoiceData);
  }, [initialInvoiceData]);

  useEffect(() => {
    if (payment) {
      createInvoice({payment_id: payment.id});
    } else if (insuranceClaim) {
      createInvoice({insurance_claim_id: insuranceClaim.id});
    }
  }, [createInvoice, payment, insuranceClaim]);

  useEffect(() => {
    if (currentInvoiceData) {
      setInvoicePDFData(undefined);
      generateInvoice({invoice_id: currentInvoiceData.id});
    }
  }, [generateInvoice, currentInvoiceData]);

  useEffect(() => {
    if (pdfData) {
      if (invoicePDFData) {
        URL.revokeObjectURL(invoicePDFData);
      }
      setInvoicePDFData(URL.createObjectURL(pdfData));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfData]);

  if (isLoading || isPending || !currentInvoiceData)
    return <Loading color="info" />;

  const {invoice_number} = currentInvoiceData;

  return (
    <Main>
      <Content>
        <Button
          startIcon={<ArrowBack />}
          onClick={navigateBack}
          sx={backButtonStyles}
        >
          {TEXT_CONSTANTS.BACK}
        </Button>
        <Divider sx={verticalDividerStyles} />
        <Typography sx={headerTitleStyles}>
          {`${TEXT_CONSTANTS.GENERATE_INVOICE} ${invoice_number}`}
        </Typography>
        <Divider sx={verticalDividerStyles} />

        <Box sx={InvoiceDetailsContentWrapperStyles}>
          <Box sx={{width: '50%'}}>
            <PatientDetails invoiceData={currentInvoiceData} />
            <Divider sx={verticalDividerStyles} />
            <InvoiceMeta
              invoiceData={currentInvoiceData}
              consultants={consultants}
              onConsultantChange={handleConsultantChange}
              showError={!currentInvoiceData?.consultant?.id}
            />
            <Divider sx={verticalDividerStyles} />
            <ItemsToBill
              invoiceData={currentInvoiceData}
              setIsSearchPaymentItemOpen={setIsSearchPaymentItemOpen}
            />
          </Box>
          <VerticalDivider />
          <Box sx={InvoiceDetailsStyles}>
            <Typography variant="h4" style={{color: Color.Purple600}}>
              {TEXT_CONSTANTS.INVOICE_PREVIEW}
            </Typography>
            <PDFViewer
              pdfData={invoicePDFData}
              isLoading={isInvoiceGenerating || !pdfData}
            />
          </Box>
        </Box>

        <Box sx={SaveInvoiceButtonWrapperStyles}>
          {!isLoading && pdfData && (
            <Tooltip
              title={
                !currentInvoiceData?.consultant?.id
                  ? TEXT_CONSTANTS.CONSULTANT_REQUIRED
                  : ''
              }
              placement="top"
            >
              <span>
                <Button
                  variant="contained"
                  color="primary"
                  sx={SaveInvoiceButtonStyles}
                  onClick={handleSaveInvoice}
                  disabled={!currentInvoiceData?.consultant?.id}
                >
                  {TEXT_CONSTANTS.SAVE_INVOICE}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

        <AddPaymentItem
          isOpen={isSearchPaymentItemOpen}
          onClose={() => setIsSearchPaymentItemOpen(false)}
          onSelect={() => setIsSearchPaymentItemOpen(false)}
          paymentId={currentInvoiceData?.payment?.id}
          insuranceClaimId={currentInvoiceData.insurance_claim?.id}
          insuranceClaim={currentInvoiceData.insurance_claim}
        />
      </Content>
    </Main>
  );
}

export default InvoiceDetailsComponent;
